import React from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { Trash2, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import {
    useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel, flexRender,
} from '@tanstack/react-table';
import { cn } from 'lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';

/**
 * Replaces react-bootstrap-table-next + react-bootstrap-table2-filter, which were
 * unmaintained and between them pulled in `underscore@1.9.1` and a Node `events`
 * shim for ~338 KB of bundle source.
 *
 * A function component because useReactTable is a hook — ProjectsCloudList stays a
 * class, keeping its lifecycle (fetchProjects / resetProjects) and delete state as
 * they were.
 */

const inputCls =
    'h-7 w-full rounded-md border border-input bg-background px-2 text-xs outline-none focus-visible:ring-1 focus-visible:ring-ring';

/* ---------------------------------------------------------------------------
   Column filters, kept as small components on purpose: swapping the date control
   back to the old single-date-plus-comparator dropdown stays a change to this file.
   --------------------------------------------------------------------------- */

const TextFilter = ({ column, placeholder }) => (
    <input
        className={inputCls}
        value={column.getFilterValue() ?? ''}
        onChange={(e) => column.setFilterValue(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        onClick={(e) => e.stopPropagation()}
    />
);

const DateRangeFilter = ({ column }) => {
    const [from, to] = column.getFilterValue() ?? [];
    // undefined rather than ['',''] so TanStack treats an empty range as "no filter"
    const set = (next) => column.setFilterValue(next[0] || next[1] ? next : undefined);
    return (
        <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
            <input type="date" className={inputCls} value={from ?? ''} aria-label="From"
                onChange={(e) => set([e.target.value, to])} />
            <input type="date" className={inputCls} value={to ?? ''} aria-label="To"
                onChange={(e) => set([from, e.target.value])} />
        </div>
    );
};

/**
 * The old dateFilter() offered one date plus a comparator (=, >, >=, <, <=, !=).
 * A From/To range expresses all of those except "not equal", which is a strange
 * query for a creation date.
 *
 * `to` is pushed to the END of its day. creationDate carries a time, so comparing
 * inclusively against 00:00 would exclude a project created at 14:32 on the very
 * day the user picked — which reads as missing data rather than as a filter.
 */
const dateRangeFilterFn = (row, columnId, value) => {
    const [from, to] = value ?? [];
    if (!from && !to) return true;
    const t = new Date(row.getValue(columnId)).getTime();
    if (Number.isNaN(t)) return false;
    if (from && t < new Date(`${from}T00:00:00`).getTime()) return false;
    if (to && t > new Date(`${to}T23:59:59.999`).getTime()) return false;
    return true;
};

const SortIcon = ({ dir }) => {
    const Icon = dir === 'asc' ? ChevronUp : dir === 'desc' ? ChevronDown : ChevronsUpDown;
    return <Icon className={cn('h-3.5 w-3.5 shrink-0', !dir && 'opacity-30')} aria-hidden="true" />;
};

const ProjectsTable = ({ projects, onAskDelete, t }) => {

    const columns = React.useMemo(() => [
        {
            accessorKey: 'name',
            header: t('Project_Name'),
            meta: { width: '40%', filter: 'text' },
            cell: (info) => (
                <Link to={`/projects/${info.row.original.id}/driver`} className="a-cloud-list">
                    {info.getValue()}
                </Link>
            ),
        },
        { accessorKey: 'description', header: t('Description'), meta: { filter: 'text' } },
        { accessorKey: 'customer', header: t('Customer'), meta: { width: '13%', filter: 'text' } },
        { accessorKey: 'orderCode', header: t('Order_code'), meta: { width: '12%', filter: 'text' } },
        {
            accessorKey: 'creationDate',
            header: t('Creation_Date'),
            meta: { width: '10%', filter: 'date' },
            filterFn: dateRangeFilterFn,
            cell: (info) => moment(new Date(info.getValue())).format('DD/MM/YYYY HH:mm'),
        },
        {
            id: 'delete',
            header: '',
            enableSorting: false,
            meta: { width: '2em' },
            cell: (info) => (
                <Trash2
                    className="delete-recent-project"
                    style={{ cursor: 'pointer', color: 'black' }}
                    size="1.25rem"
                    onClick={() => onAskDelete(info.row.original)}
                />
            ),
        },
    ], [t, onAskDelete]);

    const table = useReactTable({
        data: projects,
        columns,
        // matches the old `defaultSorted`
        initialState: { sorting: [{ id: 'creationDate', desc: true }] },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <Table>
            <TableHeader>
                {table.getHeaderGroups().map((hg) => (
                    <TableRow key={hg.id}>
                        {hg.headers.map((h) => {
                            const meta = h.column.columnDef.meta ?? {};
                            const sortable = h.column.getCanSort();
                            return (
                                <TableHead key={h.id} style={meta.width ? { width: meta.width } : undefined}>
                                    {/* filter above the label, as the old tableHeaderFormatter did */}
                                    <div className="flex flex-col gap-1">
                                        {meta.filter === 'text' && (
                                            <TextFilter column={h.column}
                                                placeholder={String(h.column.columnDef.header ?? '')} />
                                        )}
                                        {meta.filter === 'date' && <DateRangeFilter column={h.column} />}
                                        <div
                                            className={cn('flex items-center gap-1',
                                                sortable && 'cursor-pointer select-none')}
                                            onClick={sortable ? h.column.getToggleSortingHandler() : undefined}
                                        >
                                            {flexRender(h.column.columnDef.header, h.getContext())}
                                            {sortable && <SortIcon dir={h.column.getIsSorted() || null} />}
                                        </div>
                                    </div>
                                </TableHead>
                            );
                        })}
                    </TableRow>
                ))}
            </TableHeader>
            <TableBody>
                {table.getRowModel().rows.map((row) => (
                    /* `striped` from the old table */
                    <TableRow key={row.id} className="odd:bg-muted/30">
                        {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

export default ProjectsTable;
